<template>
  <section
    class="relative flex flex-wrap overflow-hidden sm-max:w-full"
    data-section-type="dual-column"
    data-id="template--coffee-guarantee"
  >
    <div class="relative flex flex-wrap w-full">
      <div
        style="background-color: #393735"
        class="flex items-center justify-center w-full lg:w-7/12 2xl:px-0 lg:px-4"
      >
        <div
          class="w-full xl:ml-[calc((100vw_-_1293px)_/_2)] xl:pl-4 max-w-lg md:max-w-xl m-auto text-left px-6 sm:px-10 md:px-14 py-8 md:py-16 space-y-3.5 xl:space-y-6 text-white"
        >
          <!-- Icons Section -->
          <div class="flex flex-row justify-between pb-2 space-x-2 space-y-5 text-left sm:space-x-5">
            <div v-for="icon in icons" :key="icon.imageAlt" class="flex flex-col items-center justify-center sm:items-start">
              <div class="flex items-center h-full mx-auto">
                <nuxt-img width="50" height="50" loading="lazy" format="webp" :src="icon.imageSrc" :alt="icon.imageAlt" class="block w-10 mx-auto" />
              </div>
              <div class="text-base sm:pt-3 sm:w-full">
                <div class="text-center" v-html="icon.title"></div>
              </div>
            </div>
          </div>

          <!-- Main Text Section -->
          <h2 class="flex flex-col text-3xl md:text-4xl md:block h-dual font-ww-medium">
            {{ firstText }}
          </h2>

          <!-- Descriptions -->
          <p v-for="(text, index) in descriptionTexts" :key="index" class="text-md">
            {{ text.description }}
          </p>
        </div>
      </div>

      <!-- Background Image -->
      <div class="w-full lg:w-5/12">
        <nuxt-img
         loading="lazy" format="webp" sizes="xs:340px sm:440px md:768px lg:1024px xl:500px 2xl:600px"
          width="970"
          height="600"
          class="object-cover w-full h-full"
          :src="desktopBackgroundImage"
          :alt="desktopBackgroundAlt"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  desktopBackgroundImage: {
    type: String,
    default: '/assets/images/52e9feec-e4ae-40fc-a8ce-36ef786779aa.png',
  },
  desktopBackgroundAlt: {
    type: String,
    default: 'background section - Coffee Guarantee',
  },
  firstText: {
    type: String,
    default: 'The Love Your Coffee Guarantee',
  },
  descriptionTexts: {
    type: Array,
    default: () => [
      'Coffee is personal. The source of beans and roasting process make each coffee unique.',
      'At Lifeboost, we want you to love your coffee so you realize the long-term benefits of drinking our clean, low acid, non-GMO, single-origin coffee.',
      'If you don’t absolutely love your Lifeboost Coffee product, let us know within 30 days of your order and we’ll exchange it for free or give you a full refund.',
      'We encourage you to open a bag of our coffee, take in the aroma, make a cup, and try it for yourself. If you don’t love it, even if the bag is opened, we’ll help you exchange it for a roast or flavor you might like better or give you a refund.',
      'The fastest way to get help with an exchange is to call us at 1-800-479-1596. Or, you can email us at support@lifeboostcoffee.com and we’ll help you with your order.',
    ],
  },
  icons: {
    type: Array,
    default: () => [
      {
        imageSrc: '/assets/images/LowAcidBeans.svg',
        imageAlt: 'Icon 1 - Low Acid Beans',
        title: 'Low Acid Beans',
        description: 'Low Acid Beans',
        width: 35,
        height:44
      },
      {
        imageSrc: '/assets/images/SubAndSave.svg',
        imageAlt: 'Icon 2 - Subscribe & Save',
        title: 'Subscribe & Save 30%+',
        description: 'Subscribe & Save 30%+',
        width: 51,
        height:43
      },
      {
        imageSrc: '/assets/images/FreeShipping.svg',
        imageAlt: 'Icon 3 - Free Shipping',
        title: 'Free Shipping',
        description: 'Free Shipping',
        width: 40,
        height:43
      },
    ],
  },
});

defineBuilderComponent({
  friendlyName: "CoffeeGuaranteePDP",
  inputs: [
    {
      name: "desktopBackgroundImage",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/52e9feec-e4ae-40fc-a8ce-36ef786779aa.png",
    },
    {
      name: "desktopBackgroundAlt",
      type: "string",
      defaultValue: "background section - Coffee Guarantee",
    },
    {
      name: "firstText",
      type: "richText",
      defaultValue: 'The Love Your Coffee Guarantee',
    },
    {
      name: "descriptionTexts",
      type: "list",
      subFields: [
        {
          name: "description",
          type: "string",
          defaultValue: "Coffee is personal. The source of beans and roasting process make each coffee unique.",
        },
      ],
      defaultValue: [
        {
          description: 'Coffee is personal. The source of beans and roasting process make each coffee unique.',
        },
        {
          description: 'At Lifeboost, we want you to love your coffee so you realize the long-term benefits of drinking our clean, low acid, non-GMO, single-origin coffee.',
        },
        {
          description: 'If you don’t absolutely love your Lifeboost Coffee product, let us know within 30 days of your order and we’ll exchange it for free or give you a full refund.',
        },
        {
          description: 'We encourage you to open a bag of our coffee, take in the aroma, make a cup, and try it for yourself. If you don’t love it, even if the bag is opened, we’ll help you exchange it for a roast or flavor you might like better or give you a refund.',
        },
        {
          description: 'The fastest way to get help with an exchange is to call us at 1-800-479-1596. Or, you can email us at support@lifeboostcoffee.com and we’ll help you with your order.'
        }
        
      ],
    },
    {
      name: "icons",
      type: "list",
      defaultValue: [
        {
          imageSrc: '/assets/images/LowAcidBeans.svg',
          imageAlt: 'Icon 1 - Low Acid Beans',
          title: 'Low Acid Beans',
          description: 'Low Acid Beans',
        },
        {
          imageSrc: '/assets/images/SubAndSave.svg',
          imageAlt: 'Icon 2 - Subscribe & Save',
          title: 'Subscribe & Save 30%+',
          description: 'Subscribe & Save 30%+',
        },
        {
          imageSrc: '/assets/images/FreeShipping.svg',
          imageAlt: 'Icon 3 - Free Shipping',
          title: 'Free Shipping',
          description: 'Free Shipping',
        },
      ],
      subFields: [
        {
          name: "imageSrc",
          type: "file",
          allowedFileTypes: ["jpeg", "jpg", "png", "webp", "svg"],
          defaultValue: "/assets/images/COGNITION_-_SECTION_2_-_ICON_1.png",
        },
        {
          name: "imageAlt",
          type: "string",
          defaultValue: "Icon 1 - Low Acid Beans",
        },
        {
          name: "title",
          type: "richText",
          defaultValue: "Low Acid Beans",
        },
        {
          name: "description",
          type: "richText",
          defaultValue: "Low Acid Beans",
        },
      ],
    },
  ],
});


</script>


<style data-shopify="">
@media (max-width: 767px) {
  .h-dual br {
    display: none;
  }
  .h-dual img {
    margin: 0 auto 10px;
  }
}
</style>
